import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { ExtractionAudit } from '../services/api';

/**
 * /check — minimal extraction-audit review page.
 *
 * Deliberately plain ("old-school academic web"): no design-system components,
 * just an inline-styled document. Lives inside the SPA so it is protected by
 * the existing Auth0 login. Data comes from GET /api/v1/extraction-audits.
 */

// ─── Diff rendering ─────────────────────────────────────────────────────────────

const SCALAR_LABELS: Record<string, string> = {
  client: 'Cliente',
  client_id: 'RUT cliente',
  order_number: 'Número de orden',
  order_date: 'Fecha de orden',
};

const PRODUCT_FIELD_LABELS: Record<string, string> = {
  product_name: 'Nombre de producto',
  quantity: 'Cantidad',
  unit_price: 'Precio unitario',
};

function show(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** A product line object as stored in added/removed diff entries. */
function lineName(line: any): string {
  if (!line || typeof line !== 'object') return show(line);
  return show(line.product_name);
}

/** Turns a diff object into readable rows. Unknown shapes fall back to <pre>. */
function renderDiff(diff: any): React.ReactNode {
  if (!diff || typeof diff !== 'object') {
    return <pre style={S.pre}>{JSON.stringify(diff, null, 2)}</pre>;
  }

  const blocks: React.ReactNode[] = [];

  // Scalar fields: client, client_id, order_number, order_date.
  for (const key of Object.keys(SCALAR_LABELS)) {
    const entry = diff[key];
    if (entry && entry.changed) {
      blocks.push(
        <div key={key} style={S.diffRow}>
          <div style={S.diffField}>{SCALAR_LABELS[key]}</div>
          <div>IA: {show(entry.ai)}</div>
          <div>Final: {show(entry.user)}</div>
        </div>
      );
    }
  }

  // Product lines. Each row shows the raw extracted text as context, then
  // what changed between the AI proposal and the user's final order.
  const products = Array.isArray(diff.products) ? diff.products : [];
  for (let i = 0; i < products.length; i++) {
    const p = products[i];
    if (!p || !p.changed) continue;
    const extracted = p.extracted_text ?? (p.user?.extracted_text) ?? (p.ai?.extracted_text);

    if (p.status === 'added') {
      blocks.push(
        <div key={`p${i}`} style={S.diffRow}>
          <div style={S.diffField}>Producto agregado por el usuario</div>
          {extracted && <div style={S.extracted}>Texto extraído: {show(extracted)}</div>}
          <div>Producto final: {lineName(p.user)}</div>
        </div>
      );
    } else if (p.status === 'removed') {
      blocks.push(
        <div key={`p${i}`} style={S.diffRow}>
          <div style={S.diffField}>Producto eliminado por el usuario</div>
          {extracted && <div style={S.extracted}>Texto extraído: {show(extracted)}</div>}
          <div>Producto que la IA proponía: {lineName(p.ai)}</div>
        </div>
      );
    } else {
      const label = PRODUCT_FIELD_LABELS[p.field] || p.field || 'campo';
      blocks.push(
        <div key={`p${i}`} style={S.diffRow}>
          <div style={S.diffField}>Producto — {label}</div>
          {extracted && <div style={S.extracted}>Texto extraído: {show(extracted)}</div>}
          <div>Asignado por IA: {show(p.ai)}</div>
          <div>Final del usuario: {show(p.user)}</div>
        </div>
      );
    }
  }

  if (blocks.length === 0) {
    // Known shape but nothing flagged — show the raw object as a fallback.
    return <pre style={S.pre}>{JSON.stringify(diff, null, 2)}</pre>;
  }
  return <>{blocks}</>;
}

// ─── Component ──────────────────────────────────────────────────────────────────

const ExtractionAuditCheck: React.FC = () => {
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get('date') || '';

  const [audits, setAudits] = useState<ExtractionAudit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!date) {
      setAudits([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getExtractionAudits(date)
      .then((rows) => {
        if (!cancelled) setAudits(rows);
      })
      .catch(() => {
        if (!cancelled) setError('No se pudieron cargar las auditorías.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date, api]);

  // The native form submits via GET-style query param on the same route.
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const input = e.currentTarget.elements.namedItem('date') as HTMLInputElement;
    if (input?.value) setSearchParams({ date: input.value });
  };

  return (
    <div style={S.page}>
      <h1 style={S.title}>Auditoría de extracciones</h1>

      <form onSubmit={onSubmit} style={S.form}>
        <input type="date" name="date" defaultValue={date} style={S.dateInput} />
        <button type="submit" style={S.button}>Ver correcciones</button>
      </form>

      {!date && (
        <p style={S.muted}>Selecciona una fecha para revisar correcciones.</p>
      )}

      {date && loading && <p style={S.muted}>Cargando...</p>}

      {date && error && <p style={S.error}>{error}</p>}

      {date && !loading && !error && audits.length === 0 && (
        <p style={S.muted}>No hay correcciones registradas para esta fecha.</p>
      )}

      {date && !loading && !error && audits.map((a) => (
        <div key={a.id} style={S.card}>
          <div style={S.cardHead}>
            <strong>
              {a.orderId != null
                ? <Link to={`/pedidos/${a.orderId}`} style={S.orderLink}>Orden #{a.orderId}</Link>
                : `Auditoría #${a.id}`}
            </strong>
            <span style={S.riskBadge}>
              Riesgo: {a.riskScore != null ? `${a.riskScore}/100` : '—'}
            </span>
          </div>

          <div style={S.meta}>
            Auditoría #{a.id}
            {a.sourceType ? ` · ${a.sourceType}` : ''}
            {a.fileName ? ` · ${a.fileName}` : ''}
            {' · '}
            {new Date(a.createdAt).toLocaleString('es-CL')}
          </div>

          {a.correctedFields.length > 0 && (
            <div style={S.badges}>
              {a.correctedFields.map((f, i) => (
                <span key={i} style={S.fieldBadge}>{f}</span>
              ))}
            </div>
          )}

          <div style={S.diffWrap}>{renderDiff(a.diff)}</div>
        </div>
      ))}
    </div>
  );
};

// ─── Inline styles (kept simple on purpose) ─────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  page: {
    maxWidth: 1000,
    margin: '0 auto',
    padding: '32px 20px',
    fontFamily: 'Georgia, "Times New Roman", serif',
    color: '#1a1a1a',
    lineHeight: 1.5,
  },
  title: { fontSize: 26, marginBottom: 20, fontWeight: 'normal' },
  form: { marginBottom: 24, display: 'flex', gap: 8, alignItems: 'center' },
  dateInput: {
    fontSize: 15,
    padding: '6px 8px',
    border: '1px solid #999',
    fontFamily: 'inherit',
  },
  button: {
    fontSize: 15,
    padding: '6px 14px',
    border: '1px solid #444',
    background: '#f0f0f0',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  muted: { color: '#666', fontStyle: 'italic' },
  error: { color: '#a00' },
  card: {
    border: '1px solid #ccc',
    borderRadius: 4,
    padding: '14px 16px',
    marginBottom: 16,
    background: '#fff',
  },
  cardHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
    fontSize: 17,
  },
  riskBadge: {
    fontSize: 13,
    padding: '2px 8px',
    background: '#eee',
    border: '1px solid #bbb',
    borderRadius: 3,
    fontFamily: 'monospace',
  },
  meta: { fontSize: 13, color: '#777', marginBottom: 8 },
  badges: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 },
  fieldBadge: {
    fontSize: 12,
    padding: '2px 7px',
    background: '#eef3f8',
    border: '1px solid #b8cbe0',
    borderRadius: 3,
    fontFamily: 'monospace',
  },
  orderLink: { color: '#1a4f8b', textDecoration: 'underline' },
  diffWrap: { borderTop: '1px dashed #ddd', paddingTop: 8 },
  diffRow: {
    marginBottom: 8,
    paddingLeft: 8,
    borderLeft: '3px solid #ddd',
  },
  diffField: { fontWeight: 'bold', fontSize: 14 },
  extracted: { fontSize: 13, color: '#555', fontStyle: 'italic' },
  pre: {
    background: '#f6f6f6',
    border: '1px solid #ddd',
    padding: 10,
    fontSize: 12,
    overflowX: 'auto',
    fontFamily: 'monospace',
  },
};

export default ExtractionAuditCheck;
